import { useState } from "react";
import { Target, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const parse = (t) => {
  const m = t.match(/SUMMARY:\s*([\s\S]*?)\s*SKILLS:\s*([\s\S]*)/i);
  return m ? { summary: m[1].trim(), skills: m[2].trim().replace(/\s*\n+\s*/g, ", ") } : null;
};

export const TailorDialog = ({ api, data, onApply }) => {
  const [open, setOpen] = useState(false);
  const [posting, setPosting] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const result = !busy && out ? parse(out) : null;

  const run = async () => {
    setBusy(true);
    setOut("");
    try {
      await api.stream("/ai/tailor", { resume: data, job_posting: posting }, setOut);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="btn-ink" onClick={() => setOpen(true)} data-testid="tailor-to-job-btn"><Target className="h-4 w-4 text-thread" /> Tailor to job</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-paper-border bg-paper" data-testid="tailor-dialog">
          <DialogHeader>
            <p className="eyebrow">Tailor to job</p>
            <DialogTitle className="font-serif text-2xl font-medium">Match this resume to a posting</DialogTitle>
            <DialogDescription>Paste the job description. The AI rewrites your summary and skills to fit, using only what's already true about you.</DialogDescription>
          </DialogHeader>
          <Textarea className="field min-h-[160px]" placeholder="Paste the full job posting here…" value={posting} onChange={(e) => setPosting(e.target.value)} data-testid="tailor-posting-input" />
          <button className="btn-thread" onClick={run} disabled={busy || posting.trim().length < 20} data-testid="tailor-run-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />} {busy ? "Tailoring…" : "Tailor my resume"}
          </button>
          {(out || busy) && (
            <div className="rounded-lg border border-thread/25 bg-white p-4 text-sm" data-testid="tailor-output">
              <p className={`whitespace-pre-wrap leading-relaxed ${busy ? "caret" : ""}`}>{out}</p>
              {result && (
                <button className="btn-thread mt-4" data-testid="tailor-apply-btn"
                  onClick={() => { onApply(result); setOpen(false); setOut(""); toast.success("Resume tailored to the job"); }}>
                  <Check className="h-4 w-4" /> Apply summary &amp; skills
                </button>
              )}
              {!busy && out && !result && <p className="mt-3 text-xs text-red-600">Could not read the AI response. Please try again.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
